import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { injectIntl } from 'react-intl';
import { minutesToTimeSpan } from '@/shared/utils/adminpulse';
import intlShape from '@/shared/utils/intlShape';
import messages from '../taskMessages';
import TaskProgressBadge, { computeProgressPercentage } from '../components/TaskProgressBadge';

const hasTasks = cell => cell && cell.totalCount > 0;
const hasMissingDuration = cell => cell && cell.tasksWithoutExecutionDurationCount > 0;

const renderMissingDurationIcon = (cell, intl) => (
  <OverlayTrigger
    placement="top"
    overlay={
      <Tooltip id={`task-progress-no-duration-${cell.totalCount}-${cell.completedCount}`}>
        {intl.formatMessage(messages.TaskProgressNoPlannedDuration, {
          count: cell.tasksWithoutExecutionDurationCount,
        })}
      </Tooltip>
    }
  >
    <i className="fa fa-exclamation-triangle text-warning" aria-hidden="true" />
  </OverlayTrigger>
);

const stopAndInvoke = handler => event => {
  event.stopPropagation();
  handler();
};

const TaskProgressPerEmployeeCell = ({
  cell,
  useExecutionDuration,
  onClick,
  onCompletedClick,
  intl,
}) => {
  const pct = computeProgressPercentage(cell, useExecutionDuration);
  const clickable = hasTasks(cell);
  const showWarning = clickable && useExecutionDuration && hasMissingDuration(cell);

  if (pct === null) {
    const emptyContent = (
      <>
        <span
          className={classNames('task-progress-badge-empty', {
            'task-progress-badge-unclickable': !clickable,
          })}
        >
          -
        </span>
        {showWarning && (
          <span className="task-progress-detail">{renderMissingDurationIcon(cell, intl)}</span>
        )}
      </>
    );

    return (
      <td className="text-center">
        {clickable ? (
          <div className="task-progress-cell clickable" onClick={onClick}>
            {emptyContent}
          </div>
        ) : (
          emptyContent
        )}
      </td>
    );
  }

  const completedPart = useExecutionDuration
    ? minutesToTimeSpan(cell.completedDurationMinutes)
    : cell.completedCount;
  const totalPart = useExecutionDuration
    ? minutesToTimeSpan(cell.totalDurationMinutes)
    : cell.totalCount;

  return (
    <td className="text-center">
      <div className="task-progress-cell clickable" onClick={onClick}>
        <TaskProgressBadge percentage={pct} />
        <span className="task-progress-detail">
          <span className="task-progress-completed" onClick={stopAndInvoke(onCompletedClick)}>
            {completedPart}
          </span>
          {' / '}
          {totalPart}
          {showWarning && <span className="margin5L">{renderMissingDurationIcon(cell, intl)}</span>}
        </span>
      </div>
    </td>
  );
};

TaskProgressPerEmployeeCell.propTypes = {
  cell: PropTypes.shape({
    completedCount: PropTypes.number,
    totalCount: PropTypes.number,
    completedDurationMinutes: PropTypes.number,
    totalDurationMinutes: PropTypes.number,
    tasksWithoutExecutionDurationCount: PropTypes.number,
  }),
  useExecutionDuration: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onCompletedClick: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

TaskProgressPerEmployeeCell.defaultProps = {
  cell: undefined,
};

export default injectIntl(TaskProgressPerEmployeeCell);
